import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  applyTdsCatalogSeo,
  applyTdsKeywordLandingSeo,
  downloadTdsPdf,
  getTdsDetailPath,
  getTdsRecordKey,
  partitionTdsByPdf,
  recordTdsView,
} from '../utils/tdsData';
import { fetchTdsCatalog, fetchTdsFilterMeta } from '../utils/tdsCatalogData';
import { getTdsKeywordLanding } from '../utils/tdsSeoLandings';
import { handleAppDownload } from '../utils/functions';
import './TdsPage.css';

const formatMaterialName = (grade) => {
  const parts = [grade.gradeName, grade.processName, grade.brandName, grade.codeName]
    .map((v) => (v && v !== 'N/A' ? String(v).trim() : ''))
    .filter(Boolean);
  return parts.join('  ') || 'N/A';
};

const buildApiSearchText = ({ searchQuery, selectedFamily, selectedProcess, selectedBrand }) => {
  const parts = [];
  if (searchQuery.trim()) parts.push(searchQuery.trim());
  if (selectedFamily !== 'ALL') parts.push(selectedFamily);
  if (selectedProcess !== 'ALL') parts.push(selectedProcess);
  if (selectedBrand !== 'ALL') parts.push(selectedBrand);
  return parts.join(' ').trim();
};

const TdsPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const keywordLanding = getTdsKeywordLanding(location.pathname);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewNotice, setPreviewNotice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('ALL');
  const [selectedProcess, setSelectedProcess] = useState('ALL');
  const [selectedBrand, setSelectedBrand] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [downloadingSlug, setDownloadingSlug] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterMeta, setFilterMeta] = useState({ families: [], processes: [], brands: [] });
  const [pdfCount, setPdfCount] = useState(0);
  const prevApiSearchRef = useRef(null);

  const apiSearch = useMemo(
    () =>
      buildApiSearchText({
        searchQuery,
        selectedFamily,
        selectedProcess,
        selectedBrand,
      }),
    [searchQuery, selectedFamily, selectedProcess, selectedBrand]
  );

  const handleDownload = async (grade) => {
    if (!grade?.hasPdf || downloadingSlug) return;
    setDownloadingSlug(grade.slug);
    try {
      await downloadTdsPdf(grade);
    } finally {
      setDownloadingSlug(null);
    }
  };

  useEffect(() => {
    if (keywordLanding) {
      applyTdsKeywordLandingSeo(keywordLanding);
    } else {
      applyTdsCatalogSeo();
    }
  }, [keywordLanding]);

  useEffect(() => {
    const q = searchParams.get('search') || searchParams.get('q');
    if (!q) return;
    setSearchQuery((prev) => (prev === q ? prev : q));
    setCurrentPage(1);
  }, [searchParams]);

  useEffect(() => {
    if (!keywordLanding?.search) return;
    setSearchQuery((prev) => (prev === keywordLanding.search ? prev : keywordLanding.search));
    setCurrentPage(1);
  }, [keywordLanding]);

  useEffect(() => {
    let cancelled = false;
    fetchTdsFilterMeta().then((meta) => {
      if (!cancelled) setFilterMeta(meta);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(apiSearch);
      if (prevApiSearchRef.current !== null && prevApiSearchRef.current !== apiSearch) {
        setCurrentPage(1);
      }
      prevApiSearchRef.current = apiSearch;
    }, 400);
    return () => clearTimeout(timer);
  }, [apiSearch]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setPreviewNotice(null);
        const {
          records: list,
          notice,
          totalPages: pages,
          totalItems: items,
          pdfCount: withPdfTotal,
        } = await fetchTdsCatalog({
          pageNumber: currentPage,
          pageSize,
          searchText: debouncedSearch,
        });

        if (!cancelled) {
          setRecords(list);
          setPdfCount(Number.isFinite(withPdfTotal) ? withPdfTotal : 0);
          setPreviewNotice(notice);
          const itemCount =
            Number.isFinite(items) && items >= 0 ? items : list.length;
          setTotalItems(itemCount);
          const pageCount =
            Number.isFinite(pages) && pages > 0
              ? pages
              : Math.max(1, Math.ceil(itemCount / pageSize) || 1);
          setTotalPages(pageCount);

          setFilterMeta((prev) => ({
            families: [...new Set([...prev.families, ...list.map((r) => r.polymerFamily)])]
              .filter(Boolean)
              .sort(),
            processes: [...new Set([...prev.processes, ...list.map((r) => r.processName)])]
              .filter(Boolean)
              .sort(),
            brands: [...new Set([...prev.brands, ...list.map((r) => r.brandName)])]
              .filter(Boolean)
              .sort(),
          }));
        }
      } catch (err) {
        console.error('TDS catalog load error:', err);
        if (!cancelled) {
          setRecords([]);
          setPreviewNotice('Could not load TDS catalog. Please refresh the page.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, Math.max(1, totalPages || 1)));
  }, [totalPages]);

  const familyOptions = filterMeta.families;
  const processOptions = filterMeta.processes;
  const brandOptions = filterMeta.brands;

  const pageNumbers = useMemo(() => {
    if (!Number.isFinite(totalPages) || totalPages <= 1) return [1];
    const delta = 2;
    const pages = [];
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);

    if (left > 1) {
      pages.push(1);
      if (left > 2) pages.push('ellipsis-left');
    }

    for (let p = left; p <= right; p += 1) pages.push(p);

    if (right < totalPages) {
      if (right < totalPages - 1) pages.push('ellipsis-right');
      pages.push(totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const startEntry = records.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endEntry =
    records.length > 0
      ? Math.min((currentPage - 1) * pageSize + records.length, totalItems)
      : 0;

  const { withPdf: recordsWithPdf, withoutPdf: recordsWithoutPdf } = useMemo(
    () => partitionTdsByPdf(records),
    [records]
  );

  const displayRowCount = recordsWithPdf.length + recordsWithoutPdf.length;
  const showPdfDivider = recordsWithPdf.length > 0 && recordsWithoutPdf.length > 0;
  const showNoPdfSectionHeader =
    recordsWithoutPdf.length > 0 && recordsWithPdf.length === 0 && pdfCount > 0;

  const renderTdsRow = (grade) => {
    const materialLabel = formatMaterialName(grade);
    return (
      <tr key={getTdsRecordKey(grade)} className={grade.hasPdf ? 'tds-row--has-pdf' : 'tds-row--no-pdf'}>
        <td data-label="Material Name" className="col-material">
          <Link
            to={getTdsDetailPath(grade)}
            className="tds-material-link tds-material-name"
            title={`${materialLabel} — view TDS PDF`}
            onClick={() => recordTdsView(grade.slug)}
          >
            {materialLabel}
          </Link>
        </td>
        <td data-label="MFI / K-Value" className="col-mfi">
          {grade.mfiValue || 'N/A'}
        </td>
        <td className="tds-action-cell col-view" data-label="View">
          <Link
            to={getTdsDetailPath(grade)}
            className={`tds-btn-view tds-btn-view--link ${grade.hasPdf ? '' : 'tds-btn-view--unavailable'}`}
            title={grade.hasPdf ? 'View TDS PDF' : 'PDF not available — click for details'}
            onClick={() => recordTdsView(grade.slug)}
          >
            <i className="far fa-eye" aria-hidden /> View
          </Link>
        </td>
        <td className="tds-action-cell col-download" data-label="Download">
          {grade.hasPdf ? (
            <button
              type="button"
              className="tds-btn-download"
              disabled={downloadingSlug === grade.slug}
              onClick={() => handleDownload(grade)}
            >
              <i
                className={`fas ${downloadingSlug === grade.slug ? 'fa-spinner fa-spin' : 'fa-download'}`}
                aria-hidden
              />{' '}
              Download
            </button>
          ) : (
            <span className="tds-btn-download tds-btn-download--disabled" title="No PDF uploaded">
              Download
            </span>
          )}
        </td>
      </tr>
    );
  };

  const safeTotalPages = Math.max(1, totalPages || 1);
  const canGoPrev = currentPage > 1 && !loading;
  const canGoNext = currentPage < safeTotalPages && !loading;

  const goToPage = (page) => {
    const target = Math.max(1, Math.min(safeTotalPages, Number(page)));
    if (target !== currentPage) setCurrentPage(target);
  };

  return (
    <>
      <section className="page-header tds-page-header">
        <div className="container">
          <h1>{keywordLanding ? keywordLanding.title.replace(' | BSB Fortrade', '') : 'All Polymer TDS'}</h1>
          <p className="tds-page-header-lead">
            {keywordLanding?.description ||
              'Download your required TDS sheet from the list below. For more details and to buy polymer, download the BSB Polymer App.'}
          </p>
          <button
            type="button"
            className="btn btn-download-app tds-page-header-app-btn"
            onClick={() => handleAppDownload('buyer')}
          >
            <i className="fas fa-download" aria-hidden />
            Download App
          </button>
        </div>
      </section>

      <div className="tds-container">
        <div className="tds-search-box">
          <div className="tds-search-grid">
            <div className="tds-input-wrapper">
              <i className="fas fa-search" aria-hidden />
              <input
                type="search"
                placeholder='Search e.g. "PVC susp", TL700, Formosa, K67…'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tds-search-input"
                aria-label="Search TDS catalog"
              />
            </div>
            <div className="tds-filter-group">
              <select
                className="tds-filter-select"
                value={selectedFamily}
                onChange={(e) => setSelectedFamily(e.target.value)}
                aria-label="Filter by polymer family"
              >
                <option value="ALL">Grade</option>
                {familyOptions.map((family) => (
                  <option key={family} value={family}>
                    {family}
                  </option>
                ))}
              </select>
              <select
                className="tds-filter-select"
                value={selectedProcess}
                onChange={(e) => setSelectedProcess(e.target.value)}
                aria-label="Filter by process name"
              >
                <option value="ALL">Process</option>
                {processOptions.map((process) => (
                  <option key={process} value={process}>
                    {process}
                  </option>
                ))}
              </select>
              <select
                className="tds-filter-select"
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                aria-label="Filter by brand name"
              >
                <option value="ALL"> Brand</option>
                {brandOptions.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="tds-filter-btn"
                onClick={() => {
                  setSelectedFamily('ALL');
                  setSelectedProcess('ALL');
                  setSelectedBrand('ALL');
                  setSearchQuery('');
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="tds-table-meta">
          <p>
            Showing {displayRowCount > 0 ? startEntry : 0} to{' '}
            {displayRowCount > 0 ? endEntry : 0} of{' '}
            {totalItems} entries
            {debouncedSearch ? ` (filtered)` : ''}
          </p>
        </div>

        {previewNotice && !loading && (
          <div className="tds-api-notice tds-api-notice--preview" role="status">
            <i className="fas fa-flask" aria-hidden />
            <p>{previewNotice}</p>
          </div>
        )}

        {loading ? (
          <div className="tds-loading">
            <i className="fas fa-spinner fa-spin" aria-hidden />
            <h3>Loading TDS from API…</h3>
            <p>Fetching grade codes, specifications, and PDF links.</p>
          </div>
        ) : displayRowCount > 0 ? (
          <>
            <div className="tds-table-wrap">
              <table className="tds-catalog-table">
                <thead>
                  <tr>
                    <th scope="col" className="col-material">Material Name</th>
                    <th scope="col" className="col-mfi">MFI / K-Value</th>
                    <th scope="col" className="col-view">View</th>
                    <th scope="col" className="col-download">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {recordsWithPdf.map(renderTdsRow)}
                  {showPdfDivider && (
                    <tr className="tds-catalog-section-divider" aria-hidden>
                      <td colSpan={4}>PDF not available</td>
                    </tr>
                  )}
                  {showNoPdfSectionHeader && (
                    <tr className="tds-catalog-section-divider" aria-hidden>
                      <td colSpan={4}>PDF not available</td>
                    </tr>
                  )}
                  {recordsWithoutPdf.map(renderTdsRow)}
                </tbody>
              </table>
            </div>
            <div className="tds-pagination">
              <div className="tds-pagination-nav" aria-label="Table pagination">
                <button
                  type="button"
                  className="tds-page-btn"
                  disabled={!canGoPrev}
                  onClick={() => goToPage(currentPage - 1)}
                  aria-label="Previous page"
                >
                  Previous
                </button>
                <div className="tds-page-numbers" role="navigation" aria-label="Page numbers">
                  {pageNumbers.map((page) =>
                    typeof page === 'string' ? (
                      <span key={page} className="tds-page-ellipsis" aria-hidden>
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        type="button"
                        className={`tds-page-number ${page === currentPage ? 'active' : ''}`}
                        onClick={() => goToPage(page)}
                        aria-label={`Page ${page}`}
                        aria-current={page === currentPage ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  className="tds-page-btn"
                  disabled={!canGoNext}
                  onClick={() => goToPage(currentPage + 1)}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
              <div className="tds-pagination-meta">
                <span className="tds-page-info">
                  Page {currentPage} of {safeTotalPages}
                </span>
                <div className="tds-page-jump">
                  <label htmlFor="tds-page-size">Per page</label>
                  <select
                    id="tds-page-size"
                    value={pageSize}
                    onChange={(e) => {
                      const nextSize = Number(e.target.value);
                      setPageSize(nextSize);
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="tds-empty-state">
            <i className="fas fa-file-circle-xmark" aria-hidden />
            <h3>{previewNotice ? 'TDS temporarily unavailable' : 'No TDS matches'}</h3>
            <p>{previewNotice || 'Try another keyword or polymer family filter.'}</p>
          </div>
        )}

      </div>
    </>
  );
};

export default TdsPage;
